import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { CheckCircle, Clock, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

export const PayRemainingBalance = () => {
  const { bookingId } = useParams();
  const [loading, setLoading] = useState(true);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [paymentComplete, setPaymentComplete] = useState(false);

  const fetchBookingDetails = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/payments/booking-balance/${bookingId}`);
      setBookingDetails(response.data);
      
      if (response.data.fully_paid) {
        setPaymentComplete(true);
      }
    } catch (error) {
      console.error('Error fetching booking:', error);
      toast.error('Booking not found');
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    fetchBookingDetails();
  }, [fetchBookingDetails]);

  const createOrder = async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/payments/create-balance-payment/${bookingId}`);
      return response.data.order_id;
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Failed to create payment order');
      throw error;
    }
  };

  const onApprove = async (data) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/payments/capture-balance-payment/${data.orderID}`);
      
      if (response.data.status === 'COMPLETED') {
        setPaymentComplete(true);
        toast.success('Payment successful! Thank you!');
      }
    } catch (error) {
      console.error('Payment capture error:', error);
      toast.error('Payment processing failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading booking details...</p>
        </div>
      </div>
    );
  }

  if (paymentComplete) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full">
          <CardHeader className="text-center bg-green-50">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <CardTitle className="text-2xl text-green-900">Payment Complete!</CardTitle>
          </CardHeader>
          <CardContent className="p-6 text-center">
            <p className="text-lg text-gray-700 mb-4">
              Thank you for your payment! Your booking is now fully paid.
            </p>
            <div className="bg-gray-50 rounded-lg p-4 text-left">
              <h3 className="font-bold mb-2">Booking Details:</h3>
              <p className="text-sm text-gray-600">Service: {bookingDetails?.service_type}</p>
              <p className="text-sm text-gray-600">Date: {bookingDetails?.date}</p>
              <p className="text-sm text-gray-600">Time: {bookingDetails?.time_slot}</p>
            </div>
            <p className="text-sm text-gray-500 mt-6">
              We look forward to serving you! If you have any questions, please contact us at aquacleanprowa@gmail.com or +61 475 854 327
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader className="bg-gradient-to-r from-teal-50 to-blue-50">
            <CardTitle className="text-2xl flex items-center gap-2">
              <CreditCard className="text-teal-600" size={28} />
              Complete Your Payment
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded mb-6">
              <div className="flex items-start gap-3">
                <Clock className="text-blue-600 mt-1" size={20} />
                <div>
                  <h3 className="font-bold text-blue-900 mb-1">Service Completed</h3>
                  <p className="text-sm text-blue-800">
                    Thank you for choosing AquaClean Pro! Please complete the remaining balance payment for your recent cleaning service.
                  </p>
                </div>
              </div>
            </div>

            {/* Booking Details */}
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="font-bold text-gray-900 mb-4">Booking Details</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Customer:</span>
                  <span className="font-medium">{bookingDetails?.customer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Service:</span>
                  <span className="font-medium">{bookingDetails?.service_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Date:</span>
                  <span className="font-medium">{bookingDetails?.date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Time:</span>
                  <span className="font-medium">{bookingDetails?.time_slot}</span>
                </div>
              </div>
            </div>

            {/* Payment Breakdown */}
            <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-lg p-6 mb-6">
              <h3 className="font-bold text-gray-900 mb-4">Payment Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Deposit Paid:</span>
                  <span className="text-green-600 font-medium">
                    ${bookingDetails?.deposit_paid?.toFixed(2)} AUD ✓
                  </span>
                </div>
                <div className="h-px bg-gray-300"></div>
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-gray-900">Remaining Balance:</span>
                  <span className="text-2xl font-bold text-teal-600">
                    ${bookingDetails?.remaining_balance?.toFixed(2)} AUD
                  </span>
                </div>
              </div>
            </div>

            {/* PayPal Payment */}
            <div className="mt-6">
              <PayPalScriptProvider options={{ 
                "client-id": PAYPAL_CLIENT_ID,
                currency: "AUD"
              }}>
                <PayPalButtons
                  style={{ 
                    layout: "vertical",
                    color: "blue",
                    shape: "rect",
                    label: "paypal"
                  }}
                  createOrder={createOrder}
                  onApprove={onApprove}
                  onCancel={() => toast.info('Payment cancelled')}
                  onError={(err) => {
                    console.error('PayPal error:', err);
                    toast.error('Payment error occurred');
                  }}
                />
              </PayPalScriptProvider>
            </div>

            <p className="text-sm text-gray-500 text-center mt-6">
              Secure payment powered by PayPal. All transactions are encrypted and secure.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
